import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert,
} from '@mui/material'
import { Add, MoreVert, Edit, RuleFolder, Archive } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listRoutes, submitRouteForApproval, archiveRoute, type RouteListItem, type ProductionRouteStatus } from '../../api/productieRute'
import { errorMessage } from '../../api/client'
import RouteFormDialog from './RouteFormDialog'

const STATUS_LABEL: Record<ProductionRouteStatus, string> = {
  Draft: 'Ciornă', PendingApproval: 'În aprobare', Activa: 'Activă', Arhivata: 'Arhivată', Respinsa: 'Respinsă',
}
const STATUS_COLOR: Record<ProductionRouteStatus, 'default' | 'success' | 'warning' | 'error'> = {
  Draft: 'default', PendingApproval: 'warning', Activa: 'success', Arhivata: 'default', Respinsa: 'error',
}

export default function RoutesPage() {
  const [formTarget, setFormTarget] = useState<RouteListItem | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: RouteListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['productie', 'rute'], queryFn: listRoutes })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['productie', 'rute'] })

  const submitMutation = useMutation({
    mutationFn: submitRouteForApproval,
    onSuccess: (r) => { invalidate(); setError(r.pendingApproval ? 'Rută trimisă la aprobare.' : 'Rută activată (niciun flux de aprobare configurat).') },
    onError: (e) => setError(errorMessage(e)),
  })
  const archiveMutation = useMutation({ mutationFn: archiveRoute, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<RouteListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'productArticleDescription', headerName: 'Produs', flex: 1, minWidth: 220,
      renderCell: (p) => `${p.row.productArticleCode} — ${p.row.productArticleDescription}` },
    { field: 'operationCount', headerName: 'Operații', width: 100, type: 'number' },
    { field: 'status', headerName: 'Status', width: 130, sortable: false, renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" /> },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Producție</Typography>
        <Typography variant="inherit" color="text.primary">Rute de producție</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Rute de producție</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Rută nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <RouteFormDialog target={formTarget} onClose={() => setFormTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status !== 'Draft'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="submit" disabled={menuAnchor.row.status !== 'Draft'}
            onClick={() => { submitMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><RuleFolder fontSize="small" /></ListItemIcon><ListItemText>Trimite la aprobare</ListItemText>
          </MenuItem>,
          <MenuItem key="archive" disabled={menuAnchor.row.status !== 'Activa'}
            onClick={() => { archiveMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Archive fontSize="small" /></ListItemIcon><ListItemText>Arhivează</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity={error?.includes('activat') || error?.includes('trimisă') ? 'info' : 'error'} onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
